import React, { useEffect, useRef, useState } from 'react';
import {useDispatch, useSelector} from "react-redux";
import { useTranslation } from 'react-i18next';

import ClearFilterBtn from "../../../application/ClearFilterBtn";

import Highcharts from 'highcharts/highstock';

import useDataSource from "../../../../hooks/useDataSource";
import useMainCanvasSize from "../../../../hooks/useMainCanvasSize";
import useWindowSize from "../../../../hooks/useWindowSize";

import {
    getDataColor
} from "../../../../utilities/ColorUtilities";

import {
    areLoadDependenciesMet,
    getFieldAlias,
    getFieldFormatters
} from "../../../../utilities/DataSourceUtilities";

import {
    formatDate,
    formatNumber,
    formatStyle, formatSuffix,
    formatText
} from "../../../../utilities/FormatUtilities";
import SettingsForm from "../settings/SettingsForm";
import {appSetModalContent} from "../../../../redux/app/appActions";
import {findComponent, findXGridComponent} from "../../../../redux/page/pageActions";

import { styled, Card, CardHeader, CardContent, LinearProgress } from '@mui/material';

const PREFIX = 'TimeSeriesChart';

const classes = {
    root: `${PREFIX}-root`,
    linearProgress: `${PREFIX}-linearProgress`,
    cardHeader: `${PREFIX}-cardHeader`
};

const Root = styled('div')((
    {
        theme
    }
) => ({
    [`&.${classes.root}`]: {
        height: 'calc(100% - 1px)'
    },

    [`& .${classes.linearProgress}`]: {
        height: '1px'
    },

    [`& .${classes.cardHeader}`]: {
        padding: '8px 8px 0px 8px',
        alignItems: 'start',
        textAlign: 'left',
        textTransform: 'none',
    }
}));

export default function TimeSeriesChart({id, configuration, xId, onLoading, onLoaded}) {

	const dispatch = useDispatch();
    const { t, i18n } = useTranslation();

	// TODO Handle Configuration changes when ComponentWrapper causes a Render
	// 1) Configuration needs to be turned into local state ( [compConfig, setCompConfig] = useState(configuration); )
	// 2) ComponentWrapper needs to flag in Redux state if it caused a change (e.g. componentFlipped)
	// 3) Because xConfig changes occur and update Redux state pages, that pages configuration object will always have latest object
	// 4) Component needs to have a dependency for when it's parent ComponentWrapper Redux state changes
	// 5) When the parent Component Wrapper Redux state changes, the component will call setCompConfig, using findComponent function on Redux state pages

    // App State
    const config = useSelector(state => state.config);
    const persist = useSelector(state => state.persist);
    const filters = useSelector(state => state.data.filters);
    const dataDate = useSelector(state => state.data.dataDate);
	const pageID = useSelector(state => state.app.pageID);
	const editXId = useSelector(state => state.page.componentEdit.xId);
	const componentUpdated = useSelector(state => state.page.componentUpdated);
	const componentWrapperEdit = useSelector(state => state.page.componentWrapperEdit);
	const pages = useSelector(state => state.page.pages);
	const pageConfigs = useSelector(state => state.config.data.pageConfigs);
	const activeRole = useSelector(state => state.persist.activeRoleName);

	// Component State
	const [compConfig, setCompConfig] = useState(configuration);
	const [title, setTitle] = useState(compConfig.name?.title ? compConfig.name.title : '');
	const [subtitle, setSubTitle] = useState(compConfig.name?.subtitle ? compConfig.name.subtitle : '');
    const [chart, setChart] = useState(null);
    const [previousClientHeight, setPreviousClientHeight] = useState(null);
    const [chartData, setChartData] = useState(null);
    const [sliceValue, setSliceValue] = useState(-2112);
    const [noDataMessage, setNoDataMessage] = useState("No Data");
    const [categoryFieldAlias, setCategoryFieldAlias] = useState(null);
    const [showProgress, setShowProgress] = useState(isFunction(onLoading) ? false : true);
	const [cardHeaderStyle, setCardHeaderStyle] = useState({});
	const [cardContentStyle, setCardContentStyle] = useState({});
	const [resizedComponent, setResizedComponent] = useState(false);

	const highchartOptions = compConfig?.highchartOptions ? compConfig?.highchartOptions : {};
	const dataSourceName = compConfig.dataSource?.name ? compConfig.dataSource?.name : '';
	const dependencies = compConfig.dataSource?.dependencies ? compConfig.dataSource?.dependencies : null;
	const categoryField = compConfig.dataSource?.fields?.categoryField ? compConfig.dataSource?.fields?.categoryField : '';
	let chartSeries = compConfig.chartSeries ? compConfig.chartSeries : [];
	const chartSeriesDynamic = compConfig?.chartSeriesDynamic !== undefined ? compConfig.chartSeriesDynamic : false;
	const styles = compConfig?.element?.styles;

	const chartRef = useRef(null);
	const dataSource = useDataSource(dataSourceName);
	const size = useMainCanvasSize();
	const windowSize = useWindowSize();

    // Loading
    useEffect(() => {
        if (dataSource.fetching === true) {
            if (isFunction(onLoading)) {
                onLoading();
            }
        }
    }, [dataSource.fetching]);

    // Loaded
    useEffect(() => {
        if (dataSource.fetching === false && dataSource.fetched === true) {
            if (isFunction(onLoaded)) {
                onLoaded();
            }
        }
    }, [dataSource.fetched]);


	// Did Component Wrapper Trigger Redux State Change?
	useEffect(() => {
		if (componentWrapperEdit.parentId && componentWrapperEdit.childId && xId) {
			if ((componentWrapperEdit.parentId === xId) || (componentWrapperEdit.childId === xId)) {
				const page = pages.find(page => page.name === pageID);
				for (let i = 0; i < pageConfigs.length; i++ ) {
					const roleConfig = page.roles.find(r => r.name === activeRole)[pageConfigs[i]];
					if (roleConfig) {
						const findResult = findComponent(xId, componentWrapperEdit.childId === xId ? componentWrapperEdit.parentId : null, roleConfig.configuration.components);
						if (findResult) {
							setCompConfig(findResult.configuration);
							break;
						}
					}
				}
			}
		}
	},[componentWrapperEdit]);

	// Component Configuration (compConfig) changed, reset state
	useEffect(() => {
		// console.log(compConfig);
		setTitle(compConfig.name?.title ? compConfig.name.title : '');
		setSubTitle(compConfig.name?.subtitle ? compConfig.name.subtitle : '');
		setResizedComponent(true);
	},[compConfig]);


	useEffect(() => {
		resizeCard();
	},[title, subtitle]);

	// componentUpdated & Resized
	useEffect(() => {
		if (xId) {
			if (componentUpdated.id === xId) {
				setCompConfig(componentUpdated.config);
				setResizedComponent(true);
			}
		}
	}, [componentUpdated]);

	// xEdit
	useEffect(() => {
		if (editXId) {
			if ((editXId === xId) && (pageID)) {

				const placeholders = {
					title: title,
					subtitle: subtitle
				}

				const settingsConfig = {
					title: 'Times Series Chart Configuration',
					parent: {
						minWidth: '400px',
						maxWidth: '400px',
						minHeight: '520px',
						maxHeight: '520px'
					},
					paper: null,
					button: null,
					content: <SettingsForm pageID={pageID} xId={xId} configuration={compConfig} schemaType={'TimeSeriesChart'} placeholders={placeholders}/>,
					tooltipContent: null
				}
				dispatch(appSetModalContent(settingsConfig));
			}
		}
	},[editXId, pageID]);

    // Data
    useEffect(() => {

        // console.log('-----------------TIME SERIES CHART---------STATUS->');
        // console.log('fetching: {0}'.format(dataSource.fetching));
        // console.log('fetched: {0}'.format(dataSource.fetched));
        // console.log('error: {0}'.format(dataSource.error));
        // console.log('category: {0}'.format(categoryField));
        // console.log('data:', dataSource.data);

        if (dataSource.error) {
            setNoDataMessage("Error");
            setChartData([]);
        }
        else if (categoryField === '') {
            setNoDataMessage("Invalid Configuration - No Category Defined");
            setChartData([]);
        }
        else if (dataSource.data && dataSource.fetched) {

            // If our dependencies are not met then we're
            // loading/no chance until they are
            if (!areLoadDependenciesMet(dependencies, filters)) {
                return;
            }

            setNoDataMessage("No Data");

            // Setup base data as filtered results or a baseline if included
            let data = dataSource.data;
            if (dataSource.includeBaselineResults && dataSource.baselines.length > 0) {
                // Find my baseline
                const baseline = dataSource.baselines.find(d => {
                    return d.id === id
                });

                // console.log('------------BASELINE------------->', baseline);
                if (baseline) {
                    let useBaseline = true;

                    // If there is a baseline then we use
                    // that value IF we can highlight
                    if (sliceValue !== -2112) {
                        if (!baseline.result.find(d => { return d[categoryField].toString().toLowerCase() === sliceValue.toString().toLowerCase() }))
                            useBaseline = false;
                    }

                    // console.log('------------------USE BASELINE---------------->', useBaseline);
                    if (useBaseline) {
                        data = baseline.result;
                    }
                }
            }

            // Rehydrate slice
            const componentFilters = filters.find(f => {
                return f.id === id && f.field === categoryField
            });

            // Get formatter for value
            // const valueFormatter = getFieldFormatters(dataSource, 'sum(budget) as value');
            // console.log('{0}'.format('Value formatter...'));
            // console.log(valueFormatter);

            if (componentFilters) {
                // TODO: Add utility or store in state since this
                // only assumes single condition - for now we'll use
                // local context however sharing across components
                // TBD will share same f.id
                // HACK:
                let condition = componentFilters.conditions[0];
                if (condition.indexOf("eq '") === 0) {
                    // String eq
                    condition = condition.substr(condition.indexOf("eq '") + 4);
                    condition = condition.substr(0, condition.length - 1);

                    setSliceValue(condition);
                } else if (condition.indexOf("eq ") === 0) {
                    // Numeric eq
                    condition = condition.substr(condition.indexOf("eq ") + 3);

                    setSliceValue(condition);
                } else if (condition.indexOf("like '") === 0) {
                    // String like
                    condition = condition.substr(condition.indexOf("like '") + 6);
                    condition = condition.substr(0, condition.length - 1);

                    if (condition.length > 2) {
                        if (condition.substr(0, 1) === '%') {
                            condition = condition.substr(1);
                        }
                        if (condition.substr(condition.length -1) === '%') {
                            condition = condition.substr(0, condition.length -1);
                        }
                    }

                    // console.log(condition);

                    setSliceValue(condition);
                } else {
                    console.log("**WARN** TimeSeriesChart '{0}' was not a recognized filter condition".format(condition));
                }
            }

            // Aliases and formatters - using for tooltips (and now for series)
            const labelAlias = getFieldAlias(dataSource, categoryField);
            const label = labelAlias !== null ? labelAlias : categoryField;
            setCategoryFieldAlias(label);

            // Update Data/Chart
            // console.log('setChartDate ->', data);
            setChartData(data);
        }
    }, [dataSource.data, dataSource.error]);

	// Update Chart Data
    useEffect(() => {
        updateChart();
    }, [chartData, sliceValue, persist.themeLight, t]);

    function isFunction(functionToCheck) {
        return functionToCheck && {}.toString.call(functionToCheck) === '[object Function]';
    }

	// Set Slice Value
    useEffect(() => {
        const componentFilters = filters.find(f => {
            return f.id === id
        });
        if (!componentFilters && sliceValue !== -2112) {
            setSliceValue(-2112);
        }
    }, [filters]);

	// Update Chart / Reflow on size changes
    useEffect( () => {
        // TODO: Size needs or window event needed...
        // console.log(chart);
        if (chart) {
            // Determine if we have crossed threshold and need to redo
            // underlying data i.e. slices etc.
            const clientHeight = chartRef.current.clientHeight;
            // console.log(clientHeight);
            // console.log(previousClientHeight);
            if (clientHeight !== previousClientHeight) {
                if (previousClientHeight < 350 && clientHeight >= 350) {
                    updateChart();
                    return;
                } else if (previousClientHeight >= 350 && clientHeight < 350) {
                    updateChart();
                    return;
                }
            }

            // setTimeout required to accurately obtain chart.renderTo.clientHeight and chart.renderTo.clientWidth to perform the reflow
            setTimeout(function() {
                chart.reflow();
				setResizedComponent(resizedComponent === true ? false : false);
            }, 300)
        }
    }, [size, windowSize, resizedComponent, cardHeaderStyle, cardContentStyle])

    const getSeriesColor = (index, value, sliceValueToCompare) => {
        // Example ramping values
        if (sliceValueToCompare === null || sliceValueToCompare == -2112) {
            // There are no selected values so we just want to ramp the colors
            return getDataColor(index, value, config.data.colorMap);
        } else {
            // We have selected values so we want not selected or the ramp color
            if (value === sliceValueToCompare) {
                return getDataColor(index, value, config.data.colorMap);
            } else {
                return persist.themeLight ? '#767676' : '#c0c2c9';
            }
        }
    }

    const formatTooltip = (formatters, label, input) => {

        // console.log('--> TimeSeriesChart: formatTooltip()');
        // console.log('FORMATTERS: ', formatters);
        // console.log('LABEL: {0} ({1})'.format(label, typeof label));
        // console.log('INPUT: {0} ({1})'.format(input, typeof input));

        let formattedValue = input, style = "", tooltip;
        if (formatters) {
            for (const formatter of formatters) {
                if (formatter.type.toLowerCase() === 'style') {
                    style = formatStyle(input, formatter.config, input, formatter.condition, true);
                } else if (formatter.type.toLowerCase() === 'number') {
                    formattedValue = formatNumber(input, formatter.config);
                } else if (formatter.type === 'date') {
                    formattedValue = formatDate(input, formatter.config);
                }
            }
        }

        if (label) {
            tooltip = "<tr><td style='text-align:left'>" + label + ":</td><td style='text-align:right'><span style=" + style + ">" + formattedValue + "</td></tr>";
        } else {
            tooltip = "<tr><td colspan='2' style=" + style + ">" + formattedValue + "</td></tr>";
        }

        // console.log('TOOLTIP: {0} ({1})'.format(tooltip, typeof tooltip));
        return tooltip;
    }

    // Responsive configuration was not working data reloads
    // const updateMiniView = () => {
    //     const clientHeight = chartRef.current.clientHeight;
    //
    //     // console.log('updateMiniView: {0}'.format(clientHeight));
    //     // console.log(chart);
    //     // console.log(chart.options);
    //     // console.log(chart.legend.display);
    //     // console.log(chart.options.navigator.enabled);
    //     // console.log(chart.options.scrollbar.enabled);
    //     // console.log(chart.options.rangeSelector.enabled);
    //     // console.log(chart.options.xAxis);
    //
    //     if (clientHeight < 350) {
    //         // if (chart.options.legend.enabled) {
    //         if (chart.legend.display) {
    //             console.log('do not show legend');
    //             chart.legend.update({
    //                 enabled: false
    //             })
    //             // chart.legend.group.hide();
    //             // chart.legend.box.hide();
    //             // chart.legend.display = false;
    //         }
    //         if (chart.options.navigator.enabled) {
    //             console.log('do not show navigator');
    //             chart.navigator.update({
    //                 enabled: false
    //             })
    //         }
    //         if (chart.options.scrollbar.enabled) {
    //             console.log('do not show scrollbar');
    //             chart.scrollbar.update({
    //                 enabled: false
    //             })
    //         }
    //         if (chart.options.rangeSelector.enabled) {
    //             console.log('do not show rangeSelector');
    //             chart.rangeSelector.update({
    //                 enabled: false
    //             })
    //         }
    //         // for (let x = 0; x < chart.options.xAxis.length; x++) {
    //         //     if (chart.options.xAxis[x].visible !== false) {
    //         //         console.log(chart.options.xAxis[x].visible);
    //         //         chart.xAxis[x].update({
    //         //             visible: false
    //         //         })
    //         //     }
    //         // }
    //     }
    // }

    const updateChart = () => {

        const data = chartData;
        const clientHeight = chartRef.current.clientHeight;
        setPreviousClientHeight(clientHeight);

        // Null Data means we haven't even checked data yet for an error, we set data on errors to []
        if (!chart && data === null)
            return;

        if (chart && data === null) {
            // No Data
            chart.update({
                series: [{
                    name: '{0}'.format(categoryField),
                    data: []
                }]
            }, true, true);
            chart.showNoData();
            return;
        }
        // console.log('DATA---------------------------->>>>', data);

        // If we have crossed a threshold..

        const series = [];
        const xAxis = [];
        const yAxis = [];

        // If we find configuration errors:
        //  1) we'll catch the error, 2) set the NoDataMessage to 'Chart Error' and 3) set Data to []
        //  4) then we'll build the Chart to display the message.
        try {
            // Setup DATETIME Category by (map) getting all categories, (map) converting to time, (filter) getting just unique dates, (sort) then sorting
            // TODO: Date/Time in single step and then use Set to get unique values
            const categories = data.map(n => (
                                new Date(n[categoryField])
                            ))
                            .map(date => date.getTime())
                            .filter((date, i, a) => a.indexOf(date) == i)
                            .sort();
            // console.log('CATEGORIES: ', categories);

            // If series is Dynamic, update chartSeries
            let dynamicSeriesNames = [];
            if (chartSeriesDynamic) {
                // Create the various Dynamic Series Names
                dynamicSeriesNames = data.map(f => f[chartSeries[0].dynamicField])
                    .filter((v, i, a) => a.indexOf(v) == i)
                    .sort();

                // Create the the Dynamic Chart Series
                let dynamicSeries = [];
                for (let i = 0; i < dynamicSeriesNames.length; i++) {
                    dynamicSeries.push({
                        "name": dynamicSeriesNames[i],
                        ...chartSeries[0]
                    });
                }
                // console.log('DYNAMICSERIES: ', dynamicSeries);

                // Load dynamicSeries into chartSeries
                chartSeries = dynamicSeries;
            }

            // Setup each Series
            // const series = [];
            let finalCategories = [];

            // BUILD Plot Lines - Data Date & Today
            const plotDataDate = new Date(dataDate).getTime();
            const plotToday = new Date().getTime();

            for (let i = 0; i < chartSeries.length; i++) {
                // For each Series, loop through distinct Categories and SUM data
                let seriesData = [];
                for (let j = 0; j < categories.length; j++) {
                    seriesData.push(
                        [
                            categories[j],
                            data.reduce((acc, val) => {
                                return (chartSeriesDynamic === true
                                            ? (new Date(val[categoryField]).getTime() === categories[j] && val[chartSeries[i].dynamicField] === chartSeries[i].name)
                                            : (new Date(val[categoryField]).getTime() === categories[j])
                                        )
                                        ? acc + (val[chartSeries[i].valueField] ? val[chartSeries[i].valueField] : 0)
                                        : acc;
                            }, 0)
                        ]
                    );
                }
                // console.log(' > SERIESDATA: ', seriesData);

                // TRIM SERIES - Remove all empty value elements at the beginning and end
                // Remove leading elements where data is zero
                const newSD = seriesData.slice(seriesData.findIndex(e => e[1] !== 0));
                // Remove trailing elements where data is zero (slice does not include last element)
                let finalSD = newSD.slice(newSD.reverse().findIndex(e => e[1] !== 0)).reverse();

                // TRIM SERIES at Data Date - Remove data where category is greater than the Data Date (e.g. Earned & Actual Cumulative costs)
                if (chartSeries[i]?.stopAtDataDate === true) {
                    // Remove leading elements where categoryField is less than or equal to Data Date
                    // Slice needs use array that has a reverse(), to slice from the end and not start
                    const preDataDateSD = finalSD.slice(finalSD.reverse().findIndex(e => e[0] === plotDataDate)).reverse();
                    finalSD = preDataDateSD;
                }

                // TRIM SERIES at Data Date - Remove data where category is less than the Data Date (e.g. Period Forecast costs)
                if (chartSeries[i]?.startAtDataDate === true) {
                    // Remove leading elements where categoryField is less than or equal to Data Date
                    // Slice needs use array that has a reverse(), to slice from the end and not start
                    const postDataDateSD = finalSD.slice(finalSD.findIndex(e => e[0] === plotDataDate));
                    finalSD = postDataDateSD;
                }

                // If data array only has one element, check data value is zero and then splice it out
                if (finalSD.length === 1) {
                    if (finalSD[0][1] === 0) {
                        finalSD.splice(-1);
                    }
                }
                // Store final Categories of values of xAxis
                const finalSeriesCategories = finalSD.map(m => m[0]);
                // console.log(' > SERIES CATEGORIES: ', finalSeriesCategories);
                finalCategories = finalCategories.concat(finalSeriesCategories);

                series.push({
                    name: (chartSeriesDynamic === true
                            ? chartSeries[i].name
                            : (chartSeries[i].valueField ? chartSeries[i].valueField : 'name'[i])
                          ),
                    type: chartSeries[i].type ? chartSeries[i].type : '',
                    color: chartSeries[i].color ? chartSeries[i].color : getSeriesColor(i, chartSeries[i].valueField, -2112),
                    dataLabels: [{
                        ...chartSeries[i].dataLabels,
                        formatter: function () {
                            // console.log('--> series.dataLabels.formatter()')
                            let formattedValue = this.y;
                            let cssProperties = "";
                            const formatters = getFieldFormatters(dataSource, chartSeries[i].valueField ? chartSeries[i].valueField : 'name'[i]);
                            for (const formatter of formatters) {
                                if (formatter.type.toLowerCase() === 'number') {
                                    formattedValue = formatNumber(formattedValue, formatter.config)
                                } else if (formatter.type.toLowerCase() === 'style') {
                                    cssProperties = formatStyle(this.y, formatter.config, this.y, formatter.condition, true);
                                }
                            }
                            return "<div style='" + cssProperties + "'>" + formattedValue + "</div>";
                        },
                        style: {fontWeight: 'normal'}
                    }],
                    marker: {...chartSeries[i].marker},
                    dashStyle: chartSeries[i]?.dashStyle !== undefined ? chartSeries[i].dashStyle : 'solid',    // https://api.highcharts.com/highcharts/6.0.4/series.line.dashStyle
                    yAxis: chartSeries[i].yAxis ? chartSeries[i].yAxis : 0,
                    data: finalSD,
                    threshold: null,
                    showInNavigator: chartSeries[i].showInNavigator ? chartSeries[i].showInNavigator : false
                });
            }
            // console.log(' > SERIES: ', series);

            // BUILD Final Categories array from all Series
            finalCategories = [...new Set(finalCategories)].sort();

            // Obtain the closestCategory to the configs desire: Today or Data Date
            let closestCategory = [];
            if (highchartOptions?.baseView === 'today' && finalCategories.length > 0) {
                // Get the closet (Category) to Today
                closestCategory = finalCategories.reduce((a, b) => {
                    return Math.abs(b - plotToday) < Math.abs(a - plotToday) ? b : a;
                });
            } else if (finalCategories.length > 0) {
                // Get the closet (Category) to Data Date
                closestCategory = finalCategories.reduce((a, b) => {
                    return Math.abs(b - plotDataDate) < Math.abs(a - plotDataDate) ? b : a;
                });
            }
            // Get the index of the closest Category
            const closestDDIndex = finalCategories.findIndex(e => e === closestCategory);

            // Setup xAxis
            // const xAxis = [];
            for (let k = 0; k < highchartOptions.xAxis.length; k++) {

                // BUILD small chart axis categories
                let axisCategories = finalCategories;
                let min = null;
                let max = null;
                const minPosition = highchartOptions.xAxis[k].min.toLowerCase() === 'year' ? 6 : (highchartOptions.xAxis[k].min.toLowerCase() === 'dozen' ? 12 : 6);
                const maxPosition = highchartOptions.xAxis[k].min.toLowerCase() === 'year' ? 6 : (highchartOptions.xAxis[k].min.toLowerCase() === 'dozen' ? 12 : 6);
                // if (clientHeight < 350) {
                //     const minIndex = closestDDIndex >= minPosition ? closestDDIndex-minPosition : 0;
                //
                //     // Reverse Categories, we're going to search backwards for a valid Category, 6 indices away
                //     const reversedFinalCategories = finalCategories.slice().reverse();  // The slice() method returns the selected elements in an array, as a new array object.
                //     const reversedClosestDDIndex = reversedFinalCategories.findIndex(e => e === closestCategory);
                //
                //     // Find Index where value is undefined, Index is BEFORE (less than) closestDDIndex, and Index is is 6 or less positions away from closestDDIndex
                //     const maxIndex = (finalCategories.length - (reversedFinalCategories.findIndex((f, i) => {
                //         return ( (f !== undefined) && (i <= reversedClosestDDIndex) && (reversedClosestDDIndex-i <= maxPosition) )
                //     })));
                //
                //     min = finalCategories[minIndex];  // Get the min category 6 intervals prior for Navigator
                //     max = finalCategories[maxIndex];  // Get the max category 5 intervals after for Navigator
                //     axisCategories = finalCategories.slice(minIndex, maxIndex);
                // }

                let minIndex = closestDDIndex - minPosition;
                let maxIndex = closestDDIndex + maxPosition;
                if (minIndex < 0)
                    minIndex = 0;
                if (maxIndex > finalCategories.length - 1)
                    maxIndex = finalCategories.length - 1;

                min = finalCategories[minIndex];
                max = finalCategories[maxIndex];
                // console.log(new Date(min));
                // console.log(new Date(max));

                // axisCategories = finalCategories; //finalCategories.slice(minIndex, maxIndex);
                xAxis.push({
                    type: "datetime",
                    title: {
                        style: {
                            color: persist.themeLight ? '#767676' : '#ffffff',
                        },
                        text: highchartOptions.xAxis[k].title.enabled ? t(categoryFieldAlias) : null
                    },
                    labels: {
                        ...(highchartOptions.xAxis[k].labels ? highchartOptions.xAxis[k].labels : {}),
                        style: {
                            color: persist.themeLight ? '#767676' : '#ffffff',
                        },
                        rotation: -45,
                        formatter: function () {
                            if (highchartOptions.xAxis[k].labels.hasOwnProperty('formatters')) {
                                let formattedValue = this.value;
                                for (const formatter of highchartOptions.xAxis[k].labels.formatters) {
                                    if (formatter.type.toLowerCase() === 'date') {
                                        formattedValue = formatDate(formattedValue, formatter.config)
                                    } else if (formatter.type.toLowerCase() === 'number') {
                                        formattedValue = formatNumber(formattedValue, formatter.config);
                                    } else if (formatter.type.toLowerCase() === 'style') {
                                        formattedValue = formatStyle(formattedValue, formatter.config, this.value, formatter.condition, true);
                                    } else if (formatter.type.toLowerCase() === 'text') {
                                        formattedValue = formatText(formattedValue, formatter.config);
                                    } else if (formatter.type.toLowerCase() === 'suffix') {
                                        formattedValue = formatSuffix(formattedValue, formatter.config);
                                    }
                                }
                                return formattedValue;
                            } else {
                                return Highcharts.dateFormat('%m/%d/%Y', this.value);
                            }
                        }
                    },
                    lineColor: highchartOptions.xAxis[k].lineColor ? highchartOptions.xAxis[k].lineColor : '#e6e6e6',
                    min: min, // highchartOptions.xAxis[k].min.toLowerCase() === 'year' ? min : null,
                    max: max, // highchartOptions.xAxis[k].max.toLowerCase() === 'year' ? max : null,
                    tickPositions: axisCategories,
                    // startOnTick: true,
                    // endOnTick: true,
                    plotLines: (k === 0 ? [(highchartOptions.xAxis[k].plotLines ? (highchartOptions.xAxis[k].plotLines.dataDate ? {
                        value: plotDataDate,
                        color: '#000000',
                        dashStyle: 'dot',
                        width: 1,
                        zIndex: 5,
                        label: {
                            text: 'Data Date',
                            align: 'left'
                        }
                    } : {}) : {}), (highchartOptions.xAxis[k].plotLines ? (highchartOptions.xAxis[k].plotLines.today ? {
                        value: plotToday,
                        color: '#000000',
                        dashStyle: 'dot',
                        width: 1,
                        zIndex: 5,
                        label: {
                            text: 'Today',
                            align: 'left'
                        }
                    } : {}) : {})] : {})
                })
            }
            // console.log(' > X-AXIS: ', xAxis);

            // Setup yAxis
            // const yAxis = [];
            for (let l = 0; l < highchartOptions.yAxis.length; l++) {
                yAxis.push({
                    ...highchartOptions.yAxis[l],
                    title: {
                        style: {
                            color: persist.themeLight ? '#767676' : '#ffffff',
                        },
                        text: highchartOptions.yAxis[l].title ? (highchartOptions.yAxis[l].title.text ? t(highchartOptions.yAxis[l].title.text) : null) : null
                    },
                    min: 0,
                    startOnTick: true,
                    endOnTick: true,
                    labels: {
                        ...(highchartOptions.yAxis[l].labels ? highchartOptions.yAxis[l].labels : {}),
                        style: {
                            color: persist.themeLight ? '#767676' : '#ffffff',
                        },
                        useHTML: highchartOptions.yAxis[l].useHTML ? highchartOptions.yAxis[l].useHTML : true,
                        formatter: function () {
                            // console.log('--> yAxis.labels.formatter()');
                            if (highchartOptions.yAxis[l].labels.hasOwnProperty('formatters')) {
                                let formattedValue = this.value;
                                for (const formatter of highchartOptions.yAxis[l].labels.formatters) {
                                    if (formatter.type.toLowerCase() === 'date') {
                                        formattedValue = formatDate(formattedValue, formatter.config)
                                    } else if (formatter.type.toLowerCase() === 'number') {
                                        formattedValue = formatNumber(formattedValue, formatter.config);
                                        // } else if (formatter.type.toLowerCase() === 'style') {
                                        //     formattedValue = formatStyle(formattedValue, formatter.config);
                                    } else if (formatter.type.toLowerCase() === 'text') {
                                        formattedValue = formatText(formattedValue, formatter.config);
                                    } else if (formatter.type.toLowerCase() === 'suffix') {
                                        formattedValue = formatSuffix(formattedValue, formatter.config);
                                    }
                                }
                                return formattedValue;
                            } else {
                                return this.value.toString();
                            }
                        }
                    }
                })
            }
            // console.log(' > Y-AXIS: ', yAxis);
        }
        catch(err) {
            setNoDataMessage("Chart Error");
            setChartData([]);
        }
        finally {

            if (!chart) {
                // console.log('** ---------- TimeSeriesChart: CREATE Chart ----------**');

                // Timeseries chart is a Highstock chart.  Features are turned off for usability if clientHeight < 350px
                // Some features need turned off from initial chart creation to avoid taking up space blank space (Legend, Scrollbar)

                const chartOptions = {
                    chart: {
                        animation: true,
                        backgroundColor: null,
                        type: highchartOptions.type,
                        panning: {
                            enabled: clientHeight >= 350 ? true : false
                        },
                        zoomType: clientHeight >= 350 ? 'x' : 'none',
                        events: {
                            load: function () {
                                // Loop through all series
                                const chart = this;
                                for (let i = 0; i < chart.series.length; i++) {
                                    // Match config series with chart series for MARKER update
                                    for (let x = 0; x < chartSeries.length; x++) {
                                        // console.log(chartSeries[x]);
                                        if ((chartSeries[x].valueField.toLowerCase() === chart.series[i].name.toLowerCase())
                                            && chartSeries[i].markerCondition) {

                                            const points = chart.series[i].points;
                                            // console.log(chart.series[i]);

                                            // For each point, find corresponding metadata
                                            points.forEach(p => {
                                                const pointData = data.find(d => new Date(d[categoryField]).getTime() === p.x);
                                                // console.log(pointData);
                                                switch (chartSeries[i].markerCondition.conditionType) {
                                                    case 'value':
                                                        if (chartSeries[i].markerCondition.conditionOperator === 'eq') {
                                                            if (pointData[chartSeries[i].markerCondition.conditionField].toString().toLowerCase() === chartSeries[i].markerCondition.conditionValue.toString().toLowerCase()) {
                                                                // http://jsfiddle.net/d_paul/oLh69sgp/1/
                                                                p.update({
                                                                    marker: {
                                                                        enabled: true,
                                                                        fillColor: 'green'
                                                                    }
                                                                }, false);
                                                            }
                                                        }
                                                        break;
                                                    default:
                                                        break;
                                                }
                                            });
                                        }
                                    }
                                }
                            }
                        }
                    },
                    credits: {
                        enabled: false
                    },
                    title: {
                        text: '', // card attribution used
                        margin: 0
                    },
                    subtitle: {
                        text: '', // card attribution used
                        margin: 0
                    },
                    lang: {
                        noData: noDataMessage
                    },
                    noData: {
                        style: {
                            color: '#303030',
                            fontSize: '15px',
                            fontWeight: 'bold'
                        }
                    },
                    navigator: {
                        enabled: highchartOptions.navigator.enabled && (clientHeight >= 350) ? highchartOptions.navigator.enabled : false,
                        adaptToUpdatedData: false
                    },
                    rangeSelector: {
                        enabled: highchartOptions.rangeSelector.enabled && (clientHeight >= 350) ? highchartOptions.rangeSelector.enabled : false,
                        allButtonsEnabled: true,
                        buttons: [
                            {
                                type: 'month',
                                count: 1,
                                text: '1m'
                            },
                            {
                                type: 'month',
                                count: 3,
                                text: '3m'
                            },
                            {
                                type: 'month',
                                count: 6,
                                text: '6m'
                            },
                            {
                                type: 'ytd',
                                text: 'YTD'
                            },
                            {
                                type: 'year',
                                count: 1,
                                text: '1y'
                            },
                            {
                                type: 'all',
                                text: 'All'
                            }
                        ],
                        inputEnabled: highchartOptions.rangeSelector.inputEnabled ? highchartOptions.rangeSelector.inputEnabled : true,
                        inputStyle: {
                            color: persist.themeLight ? '#494949' : '#c1c1c1',
                        },
                        inputDateFormat: '%m-%d-%Y',
                        inputEditDateFormat: '%m-%d-%Y'
                    },
                    scrollbar: {
                        enabled: highchartOptions.scrollbar.enabled && (clientHeight >= 350) ? highchartOptions.scrollbar.enabled : false,
                        liveRedraw: true,
                        height: 10,
                        margin: 5,
                        barBackgroundColor: persist.themeLight ? '#c1c1c1' : '#7d7d7d',
                        barBorderRadius: 5,
                        barBorderWidth: 0,
                        buttonBackgroundColor: persist.themeLight ? '#c1c1c1' : '#7d7d7d',
                        buttonBorderWidth: 0,
                        buttonArrowColor: persist.themeLight ? '#7d7d7d' : '#fafafa',
                        buttonBorderRadius: 5,
                        rifleColor: persist.themeLight ? '#c1c1c1' : '#7d7d7d',
                        trackBackgroundColor: '#fafafa',
                        trackBorderWidth: 5,
                        trackBorderColor: '#fafafa',
                        trackBorderRadius: 0
                    },
                    legend: {
                        // enabled: highchartOptions.legend.enabled && (clientHeight >= 350) ? highchartOptions.legend.enabled : false,
                        enabled: highchartOptions.legend.enabled ? highchartOptions.legend.enabled : false,
                        itemStyle: {
                            fontSize: '10px',
                            fontWeight: 'normal',
                            color: persist.themeLight ? '#767676' : '#ffffff',
                        },
                        verticalAlign: 'bottom',
                        labelFormatter: function () {
                            let label = getFieldAlias(dataSource, this.name);
                            return label ? label : this.name;
                        }
                    },
                    plotOptions: {
                        ...(highchartOptions.plotOptions ? highchartOptions.plotOptions : {})
                    },
                    series: series,
                    tooltip: {
                        ...(highchartOptions.tooltip ? highchartOptions.tooltip : {}),
                        borderColor: '#000000',
                        shared: true,
                        useHTML: true,
                        formatter: function () {
                            // console.log(' > TimeSeriesChart: CREATE Chart: tooltip.formatter(): ', this.points);
                            let formattedTooltip = '<table>';
                            // Category
                            formattedTooltip += formatTooltip(
                                getFieldFormatters(dataSource, categoryField),
                                null,
                                this.x // Highcharts.dateFormat('%m/%d/%Y', this.x)
                            );
                            // Series
                            for (let i = 0; i < this.points.length; i++) {
                                const tooltipSeries = chartSeries.find(c => (chartSeriesDynamic === true ? c.name : c.valueField) === this.points[i].series.name);

                                let useFormatter = true;
                                if (tooltipSeries?.useFormatter !== undefined) {
                                    if (tooltipSeries.useFormatter !== true) {
                                        useFormatter = false;
                                    }
                                }
                                const label = getFieldAlias(dataSource, this.points[i].series.name);
                                formattedTooltip += formatTooltip(
                                    useFormatter ? getFieldFormatters(dataSource, this.points[i].series.name) : null,
                                    t(label !== null ? label : this.points[i].series.name),
                                    this.points[i].y
                                );
                            }
                            formattedTooltip += '</table>';
                            return formattedTooltip;
                        }
                    },
                    xAxis: xAxis,
                    yAxis: yAxis,
                    responsive: {
                        rules: [{
                            condition: {
                                maxHeight: 349
                            },
                            chartOptions: {
                                // legend: {
                                //     enabled: false
                                // },
                                navigator: {
                                    enabled: false
                                },
                                rangeSelector: {
                                    enabled: false
                                },
                                scrollbar: {
                                    enabled: false
                                }
                                // xAxis: {
                                //     visible: false
                                // }
                            }
                        }, {
                            condition: {
                                minHeight: 350
                            },
                            chartOptions: {
                                // legend: {
                                //     enabled: highchartOptions.legend.enabled ? highchartOptions.legend.enabled : false
                                // },
                                navigator: {
                                    enabled: highchartOptions.navigator.enabled ? highchartOptions.navigator.enabled : false
                                },
                                rangeSelector: {
                                    enabled: highchartOptions.rangeSelector.enabled ? highchartOptions.rangeSelector.enabled : false
                                },
                                scrollbar: {
                                    enabled: highchartOptions.scrollbar.enabled ? highchartOptions.scrollbar.enabled : false
                                },
                                xAxis: {
                                    // visible: true,
                                    labels: {
                                        enabled: highchartOptions.xAxis[0].labels.enabled ? highchartOptions.xAxis[0].labels.enabled : false
                                    }
                                }
                            }
                        }]
                    }
                };

                // console.log('** ---------- TimeSeriesChart: CREATE Chart (chartOptions) ----------**', chartOptions);
                setChart(Highcharts.stockChart(chartRef.current, chartOptions));
            } else {
                // console.log('** ---------- TimeSeriesChart: UPDATE Chart ----------**');

                chart.update({
                    // NOTE: We need to update our handlers otherwise they will point to
                    // a previously defined function with a context that does not match
                    // the current state - to reproduce comment out plot options
                    // filter once chart, clear, then filter the 2nd chart and all
                    // previously applied filters will be displayed
                    lang: {
                        noData: noDataMessage
                    },
                    noData: {
                        style: {
                            color: '#303030',
                            fontSize: '15px',
                            fontWeight: 'bold'
                        }
                    },
                    scrollbar: {
                        enabled: highchartOptions.scrollbar.enabled && (clientHeight >= 350) ? highchartOptions.scrollbar.enabled : false,
                        liveRedraw: true,
                        height: 10,
                        margin: 5,
                        barBackgroundColor: persist.themeLight ? '#c1c1c1' : '#7d7d7d',
                        barBorderRadius: 5,
                        barBorderWidth: 0,
                        buttonBackgroundColor: persist.themeLight ? '#c1c1c1' : '#7d7d7d',
                        buttonBorderWidth: 0,
                        buttonArrowColor: persist.themeLight ? '#7d7d7d' : '#fafafa',
                        buttonBorderRadius: 5,
                        rifleColor: persist.themeLight ? '#c1c1c1' : '#7d7d7d',
                        trackBackgroundColor: '#fafafa',
                        trackBorderWidth: 5,
                        trackBorderColor: '#fafafa',
                        trackBorderRadius: 0
                    },
                    legend: {
                        // enabled: highchartOptions.legend.enabled && (clientHeight >= 350) ? highchartOptions.legend.enabled : false,
                        enabled: highchartOptions.legend.enabled ? highchartOptions.legend.enabled : false,
                        itemStyle: {
                            fontSize: '10px',
                            fontWeight: 'normal',
                            color: persist.themeLight ? '#767676' : '#ffffff',
                        },
                        verticalAlign: 'bottom',
                        labelFormatter: function () {
                            let label = getFieldAlias(dataSource, this.name);
                            return label ? label : this.name;
                        }
                    },
                    plotOptions: {
                        ...(highchartOptions.plotOptions ? highchartOptions.plotOptions : {}),
                    },
                    series: series,
                    xAxis: xAxis,
                    yAxis: yAxis
                }, false);

                // for (let x = 0; x < xAxis.length; x++) {
                //     chart.xAxis[x].update({
                //        xAxis: xAxis[x]
                //     }, false);
                // }

                // updateMiniView();

                setTimeout(function () {
                    chart.redraw();
					chart.reflow();
					setResizedComponent(resizedComponent === true ? false : false);
                }, 300)
            }
        }
    };

    // function findIndexOfGreatest(array) {
    //     let greatest;
    //     let indexOfGreatest;
    //     for (let i = 0; i < array.length; i++) {
    //         if (!greatest || array[i] > greatest) {
    //             greatest = array[i];
    //             indexOfGreatest = i;
    //         }
    //     }
    //     return indexOfGreatest;
    // }

	const resizeCard = () => {

		const newCardHeaderStyle = Object.assign({}, {...styles?.cardHeader});
		const newCardContentStyle = Object.assign({}, {...styles?.cardContent
			,height: 'calc(100% - 49px)'
			,overflow: 'auto'
			,padding: '0px'}
		);

		if (title && subtitle) {
			// Default Style
		} else if (title) {
			// Title Only
			newCardHeaderStyle.height = '29px';
			newCardContentStyle.height = 'calc(100% - 29px)';
		} else if (subtitle) {
			// Subtitle Only
			newCardHeaderStyle.height = '29px';
			newCardContentStyle.height = 'calc(100% - 29px)';
		} else {
			// No title
			newCardHeaderStyle.height = '0px';
			newCardHeaderStyle.padding = '0px';
			newCardContentStyle.height = '100%';
		}
		setCardHeaderStyle(newCardHeaderStyle);
		setCardContentStyle(newCardContentStyle);
	}

    return (
        <Root className={classes.root}>
            {showProgress && dataSource.fetching &&
                <LinearProgress className={classes.linearProgress}/>
            }
            {showProgress && !dataSource.fetching &&
                <div className={classes.linearProgress}></div>
            }
            <Card style={{height: '100%', boxShadow: 'none', ...styles?.card}}>
                <CardHeader action={ClearFilterBtn(id, false)} className={classes.cardHeader} style={cardHeaderStyle}
                            titleTypographyProps={{color: 'textPrimary', variant: 'subtitle2'}}
                            title={t(title)}
                            subheaderTypographyProps={{color: 'textSecondary', variant: 'body2'}}
                            subheader={t(subtitle)}></CardHeader>
                <CardContent style={cardContentStyle}>
                    <div id={"tscomp"} ref={chartRef} style={{position: 'relative', top: '0px', width: '100%', height: '100%'}}></div>
                </CardContent>
            </Card>
        </Root>
    );
}
